import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK, formatHours } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { DebriefSummary } from "@/lib/project-report-calc";
import type { DBProject } from "@/lib/db-types";

interface Props {
  debrief: DebriefSummary;
  project: DBProject;
  isLoading: boolean;
}

/**
 * Debrief-delen av den interne prosjektrapporten (VIB-1068): baseline
 * (immutabelt snapshot fra budget_line_baseline) vs. faktisk budsjett, med
 * slettede/nye linjer og prosjektets debrief-status/notater.
 */
export function ReportDebriefSection({ debrief, project, isLoading }: Props) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center h-24">
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const topRows = debrief.rows.slice(0, 10);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Debrief — baseline vs. faktisk budsjett</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {debrief.hasBackfilledBaseline && (
          <p className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-500/40 rounded-md p-2">
            <SrIcon name="triangle-alert" className="w-4 h-4 flex-shrink-0" />
            Baseline for dette prosjektet er backfillet (eldre enn snapshot-fangsten
            24.06.2026) og kan avvike fra det opprinnelige estimatet — debrief-basisen
            er upresis.
          </p>
        )}

        {(project.debrief_status || project.debrief_notes) && (
          <div className="text-sm space-y-1">
            {project.debrief_status && (
              <p>
                <span className="text-muted-foreground">Debrief-status:</span>{" "}
                {project.debrief_status}
                {project.debrief_reason ? ` (${project.debrief_reason})` : ""}
              </p>
            )}
            {project.debrief_notes && (
              <p className="text-muted-foreground whitespace-pre-wrap">{project.debrief_notes}</p>
            )}
          </div>
        )}

        {topRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ingen baseline-linjer å sammenligne (prosjektet mangler snapshot).
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2">Linje (størst avvik først)</th>
                <th className="text-right py-2">Timer (baseline → nå)</th>
                <th className="text-right py-2">Beløp (baseline → nå)</th>
                <th className="text-right py-2">Avvik</th>
              </tr>
            </thead>
            <tbody>
              {topRows.map((r, i) => (
                <tr
                  key={r.budgetLineId}
                  className={cn("border-b border-border/50 last:border-0", i === 0 && r.deviationAmountKr !== 0 && "bg-amber-50/60")}
                >
                  <td className="py-2">{r.description}</td>
                  <td className="py-2 text-right tabular-nums">
                    {formatHours(r.baselineHours)} → {formatHours(r.currentHours)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatNOK(r.baselineAmountKr)} → {formatNOK(r.currentAmountKr)}
                  </td>
                  <td
                    className={cn(
                      "py-2 text-right tabular-nums",
                      r.deviationAmountKr > 0 ? "text-emerald-700" : r.deviationAmountKr < 0 ? "text-red-600" : "text-muted-foreground",
                    )}
                  >
                    {r.deviationAmountKr > 0 ? "+" : ""}
                    {formatNOK(r.deviationAmountKr)}
                    {r.deviationHoursPct != null && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({r.deviationHoursPct > 0 ? "+" : ""}
                        {r.deviationHoursPct} % t)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {(debrief.removed.length > 0 || debrief.added.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            {debrief.removed.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Fjernet fra budsjett
                </p>
                <ul className="space-y-1">
                  {debrief.removed.map((b) => (
                    <li key={b.budget_line_id} className="text-muted-foreground">
                      {b.description ?? "—"} ({formatNOK(Number(b.amount ?? 0))})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {debrief.added.length > 0 && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Lagt til etter oppstart
                </p>
                <ul className="space-y-1">
                  {debrief.added.map((l) => (
                    <li key={l.id} className="text-muted-foreground">
                      {l.description ?? "—"} ({formatNOK(Number(l.amount ?? 0))})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
